import { todayISO } from '../shared/functions/dateUtils.js'

const _today = new Date()

// Static config arrays — outside state to avoid signal wrapping.
export const DOC_FOLDERS = Object.freeze([
  { key: 'all', label: 'All documents', icon: 'folder' },
  { key: 'company', label: 'Company', icon: 'flag' },
  { key: 'legal', label: 'Legal', icon: 'bookmark' },
  { key: 'finance', label: 'Finance', icon: 'document' },
  { key: 'people', label: 'People & HR', icon: 'people' },
  { key: 'engineering', label: 'Engineering', icon: 'code' },
  { key: 'design', label: 'Design', icon: 'eye' }
])

export const KB_CATEGORIES = Object.freeze([
  { key: 'all', label: 'All articles', icon: 'document' },
  { key: 'getting-started', label: 'Getting Started', icon: 'star' },
  { key: 'engineering', label: 'Engineering', icon: 'code' },
  { key: 'design', label: 'Design System', icon: 'eye' },
  { key: 'policies', label: 'Policies', icon: 'bookmark' },
  { key: 'tools', label: 'Tools & Infra', icon: 'settings' }
])

export default {
  isRootState: true,

  currentUserEmail: '',
  currentUserId: '',
  signedIn: false,
  companyName: 'Symbols',

  // Auth claims + org context — populated by subscribeAuthSession from the
  // governance JWT. Without these declared, smbls 3.14 silently drops the
  // updates and org switching / People filter / role gates all break.
  jwtClaims: null,
  orgs: [],
  workspaces: [],
  org: null,
  workspace: null,
  _wsSwitching: null,
  _wsRefreshing: false,
  _loginLoading: false,
  orgNotifCount: {},
  orgNotifSignals: {},
  // Financials project data (PROJECT_FINANCIALS optional federation).
  companySettings: null,
  userGrants: [],
  valuations: [],

  selectedGroup: null,
  selectedRole: null,
  currentPath: typeof window !== 'undefined' ? window.location.pathname : '/',
  navSearchQuery: '',
  navSearchResults: [],
  _navSearchGrouped: [],
  _navSearchLoading: false,
  _navSearchOpen: false,
  navUnreadCounts: {},
  contentSection: null,

  people: [],
  peopleAll: [],
  teams: [],
  teamsByEmail: {},
  orgMembersByOrgId: {},
  userDocs: [],
  userPrefs: null,
  resourceLinks: [],
  _wsMenuOpen: false,
  announcements: [],
  selectedAnnouncement: null,
  stories: [],
  birthdays: [],
  companyInfo: [],
  roles: { available: [], byUser: {}, version: 0 },

  // ── Calendar ──
  viewMode: 'week',
  currentYear: _today.getFullYear(),
  currentMonth: _today.getMonth(),
  currentDay: _today.getDate(),
  currentMinutes: _today.getHours() * 60 + _today.getMinutes(),
  selectedEvent: null,
  showNewBooking: false,
  showEventDetail: false,
  selectedPerson: null,
  profileEmail: null,
  profileModalTab: 'profile', // 'profile' | 'availability' | 'settings' — used by the unified ProfileModal
  dragPreview: null,
  quickCreate: null,
  hoverTime: null,
  teamMembers: [],
  onlineUsers: [],
  newBooking: {
    title: '',
    date: todayISO(),
    startTime: '09:00',
    endTime: '10:00',
    invitees: '',
    inviteeQuery: '',
    description: '',
    type: 'meeting',
    meetingMode: 'symbols-meet',
    recurring: 'none',
    recurringDays: [],
    editingEventId: null
  },
  events: [],

  // ── Tickets ──
  columns: [],
  tickets: [],
  loadedByColumn: {},
  totalByColumn: {},
  loadingColumn: {},
  epicOptions: [],
  epicCounts: {},
  myTickets: [],
  recentTickets: [],
  myViewQueueOpen: false,
  _activityData: {},
  _todayUpdate: null,
  _dailyDraft: '',
  _dailyUpdates: [],
  _standupMessages: [],
  _standupUserName: '',
  _standupHistory: {},
  _standupPhase: 'idle',
  _standupSummary: '',
  _standupActions: [],
  _standupFollowUps: [],
  _ticketComments: [],
  _mentionModal: { open: false },
  filterAssignee: null,
  filterLabel: null,
  searchQuery: '',

  // ── Requests ──
  customerRequests: [
    { id: 1, title: 'Add dark mode to mobile app', source: 'customer', status: 'open', priority: 'high', author: 'john@client.com', createdAt: '2026-04-05', votes: 12, tags: ['ux', 'mobile'] },
    { id: 2, title: 'Export data as CSV', source: 'customer', status: 'open', priority: 'medium', author: 'sarah@partner.co', createdAt: '2026-04-03', votes: 8, tags: ['feature', 'data'] },
    { id: 3, title: 'SSO login with Google Workspace', source: 'customer', status: 'in-progress', priority: 'high', author: 'ops@enterprise.io', createdAt: '2026-03-28', votes: 23, tags: ['auth', 'enterprise'] },
    { id: 4, title: 'API rate limiting too aggressive', source: 'customer', status: 'resolved', priority: 'low', author: 'dev@startup.com', createdAt: '2026-03-20', votes: 5, tags: ['api', 'bug'] }
  ],
  inboundRequests: [
    { id: 5, title: 'Partnership inquiry — Figma plugin', source: 'inbound', status: 'new', channel: 'email', author: 'partnerships@figma.com', createdAt: '2026-04-08', summary: 'Interested in building a Symbols plugin for Figma.' },
    { id: 6, title: 'Enterprise demo request', source: 'inbound', status: 'scheduled', channel: 'form', author: 'cto@bigcorp.com', createdAt: '2026-04-06', summary: '50-seat deployment, needs SSO + audit logs.' },
    { id: 7, title: 'Bug report — calendar sync', source: 'inbound', status: 'triaged', channel: 'support', author: 'user@gmail.com', createdAt: '2026-04-04', summary: 'Google Calendar events not syncing after timezone change.' }
  ],
  outboundRequests: [
    { id: 8, title: 'Follow up — Q1 feature rollout', source: 'outbound', status: 'sent', channel: 'email', recipient: 'all-customers@symbols.app', createdAt: '2026-04-01', summary: 'Announcing calendar sync, chat threads, document editor.' },
    { id: 9, title: 'Proposal — Enterprise tier pricing', source: 'outbound', status: 'draft', channel: 'email', recipient: 'cto@bigcorp.com', createdAt: '2026-04-07', summary: 'Custom pricing for 50+ seats with dedicated support.' }
  ],
  auditReports: [
    { id: 10, title: 'Q1 2026 Support Metrics', type: 'report', status: 'published', createdAt: '2026-04-01', summary: '142 tickets resolved, avg 4.2h response time, 96% satisfaction.' },
    { id: 11, title: 'Security Audit — Auth Flow', type: 'audit', status: 'in-review', createdAt: '2026-03-25', summary: 'Reviewed auth middleware, session handling, token rotation.' },
    { id: 12, title: 'Accessibility Compliance Check', type: 'audit', status: 'draft', createdAt: '2026-04-08', summary: 'WCAG 2.1 AA compliance across governance app.' }
  ],

  // ── Resolutions ──
  suggestions: [
    { id: 20, title: 'Reduce ticket backlog by auto-assigning', source: 'analytics', impact: 'high', confidence: 0.87, createdAt: '2026-04-08', summary: 'Analysis shows 34% of backlog tickets have no assignee. Auto-assign based on expertise tags.' },
    { id: 21, title: 'Merge duplicate channels in chat', source: 'analytics', impact: 'medium', confidence: 0.72, createdAt: '2026-04-06', summary: '3 channels have overlapping topics (#general, #team-updates, #announcements). Suggest consolidation.' },
    { id: 22, title: 'Schedule recurring standup reminder', source: 'analytics', impact: 'low', confidence: 0.91, createdAt: '2026-04-05', summary: 'Team standup attendance dropped 15% — automated calendar reminder could help.' }
  ],
  improvementRequests: [
    { id: 30, title: 'Analytics dashboard with ticket velocity', prompt: 'Create an analytics dashboard showing ticket velocity, burndown, and team capacity', author: 'nika@symbols.app', model: 'claude-haiku', status: 'pending', createdAt: '2026-04-09' },
    { id: 31, title: 'Compact calendar week view', prompt: 'Create a compact week view showing only business hours', author: 'luka@symbols.app', model: 'gemini-3.1', status: 'approved', createdAt: '2026-04-07' },
    { id: 32, title: 'Team workload heatmap', prompt: 'Create a heatmap showing team member workload across projects', author: 'tornike@symbols.app', model: 'claude-haiku', status: 'rejected', createdAt: '2026-04-04' }
  ],
  editRequests: [
    { id: 40, title: 'Fix responsive layout on calendar mobile', repo: 'symbo-ls/smbls', branch: 'fix/calendar-mobile', author: 'tornike@symbols.app', status: 'open', reviewers: ['nika@symbols.app'], createdAt: '2026-04-09', additions: 42, deletions: 18 },
    { id: 41, title: 'Add virtual scroll to chat message list', repo: 'symbo-ls/gov', branch: 'feat/virtual-scroll', author: 'luka@symbols.app', status: 'merged', reviewers: ['nika@symbols.app', 'thomas@symbols.app'], createdAt: '2026-04-07', additions: 156, deletions: 23 },
    { id: 42, title: 'Update design system color tokens', repo: 'symbo-ls/smbls', branch: 'chore/color-tokens', author: 'nika@symbols.app', status: 'open', reviewers: ['luka@symbols.app'], createdAt: '2026-04-08', additions: 8, deletions: 8 }
  ],
  selectedTicket: null,
  ticketDraft: null,
  ticketEditMode: false,
  activeDrag: null,
  dropPreview: null,
  syncing: false,

  // ── Chat ──
  channels: [],
  channelMembers: [],
  activeChannelId: null,
  showNewChannel: false,
  newChannelDraft: { name: '', topic: '', kind: 'channel', is_private: false, members: [] },
  messagesByChannel: {},
  activeThreadRootId: null,
  presence: {},
  typingByChannel: {},
  reactionsByMessage: {},
  myMentions: [],
  collapsedGroups: {},
  searchOpen: false,
  searchResults: [],
  mentionPicker: null,
  sidebarDropdown: { open: false },
  _hiddenChannels: [],
  _pinnedChannels: [],
  renameModal: { open: false },
  pendingAttachments: {},
  composerDraft: {},
  composerPreview: false,
  chatVisibleCounts: null,
  activeTab: 'conversation',
  showFilterBar: false,
  filters: {
    authorEmail: null,
    hasAttachments: false,
    hasMentions: false,
    query: ''
  },

  // ── Documents ──
  activeSection: 'documents',
  selectedFolder: 'all',
  selectedDocument: null,
  docsQuery: '',
  documents: [],
  selectedNoteId: null,
  notesFilter: 'all',
  notes: [],
  editorDoc: null,
  editorMode: null,
  kbQuery: '',
  selectedCategory: 'all',
  kbArticles: [],

  // ── Lightbox ──
  lightbox: null,

  // ── AI Assistant ──
  assistantOpen: false,
  assistantMessages: [],
  assistantTyping: false,
  assistantAttachment: null,

  // ── Notifications ──
  notifications: [],
  notificationsPanelOpen: false
}
